/**
 * Templates source pane (S7, live-wired S7-R M1) — search + a per-engine
 * catalog of canned, connection-validated DBA queries. Selecting a card
 * SELECTS the template (S24 polish): the dialog's right half swaps to
 * `TemplateDetailPane`, which shows the full description, the SQL, the
 * permissions it needs, and the explicit "Use template" action that runs the
 * ONE insertion path (`useTemplateInsertion`). The catalog is a teaching
 * surface, not a black box — nothing is inserted behind the user's back.
 *
 * Card copy (S24 polish): the catalog packs the grant/permission caveat into
 * the same `description` string, so three of four cards in a category used to
 * repeat one identical sentence verbatim (and rendered ragged heights). Cards
 * clamp the LEAD to two lines and park the caveat behind a lock glyph +
 * tooltip — `splitTemplateDescription` does the display-layer split.
 *
 * Non-runnable templates (minimumVersion/requiredCapabilities not met) are
 * filtered OUT by `availableTemplates` before this component ever sees
 * them — hidden, never greyed, per the plan's quality bar.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useConnectionStore } from '../../../../connections/store/connectionStore';
import { fetchServerVersionInfo } from '../../../../connections/services/serverInfoService';
import type { DashboardEngineId } from '../../types/dashboardTypes';
import type { DashboardTemplate } from '../../templates/templateTypes';
import { availableTemplates } from '../../templates/templateCatalog';
import { templateCapabilities } from '../../templates/templateCapabilities';
import { splitTemplateDescription } from './templateDescription';
import { CATEGORY_LABELS, FIT_LABELS } from './templateLabels';

const FIFTEEN_MINUTES = 15 * 60 * 1000;

const KNOWN_ENGINES: ReadonlySet<string> = new Set([
  'mssql',
  'pgsql',
  'mysql',
  'oracle',
  'sqlite',
  'kusto',
]);

function TemplateCard({
  template,
  selected,
  onSelect,
}: {
  template: DashboardTemplate;
  selected: boolean;
  onSelect: (t: DashboardTemplate) => void;
}) {
  const { lead, requirement } = splitTemplateDescription(template.description);
  return (
    <button
      type="button"
      onClick={() => onSelect(template)}
      aria-pressed={selected}
      className={`flex h-full flex-col items-start gap-1 rounded-md border px-2.5 py-2 text-left transition-colors hover:bg-accent ${
        selected ? 'border-primary bg-primary/5' : 'border-border'
      }`}
      data-testid="template-card"
      data-template-id={template.id}
      data-category={template.category}
      data-selected={selected || undefined}
    >
      <span className="text-xs font-medium text-foreground">{template.title}</span>
      {/* Clamped to two lines: every card in a category then shows the same
          amount of the sentence that actually differentiates it, and the grid
          rows stop laddering. Full text lives in the detail pane. */}
      <span className="line-clamp-2 text-[11px] leading-snug text-muted-foreground">{lead}</span>
      <span className="mt-auto flex items-center gap-1 pt-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {CATEGORY_LABELS[template.category]} · {FIT_LABELS[template.defaultTileType]}
        {requirement && (
          <span
            title={requirement}
            aria-label={`Needs permissions: ${requirement}`}
            data-testid="template-card-permission-glyph"
          >
            <Lock className="h-2.5 w-2.5" aria-hidden />
          </span>
        )}
      </span>
    </button>
  );
}

export function TemplatesPane({
  connectionId,
  selectedTemplateId,
  onSelectTemplate,
}: {
  connectionId: string;
  /** Currently previewed template (detail pane owns the insertion action). */
  selectedTemplateId: string | null;
  onSelectTemplate: (template: DashboardTemplate) => void;
}) {
  const [query, setQuery] = useState('');
  const active = useConnectionStore((s) => s.activeConnections).find((c) => c.id === connectionId);
  const engineRaw = active?.connection.engine;
  const engine: DashboardEngineId | undefined = KNOWN_ENGINES.has(engineRaw ?? '')
    ? (engineRaw as DashboardEngineId)
    : undefined;
  const database = active?.connection.database;

  // Live server version, gating minimumVersion templates (S7-R M1). Kusto is
  // skipped: it has no SQL version() probe (its own `.show version` is a
  // different query shape entirely) and no shipped kusto template gates on
  // version — capability gating (below) covers kusto instead. Errors
  // (including a mock connection with no seeded version response) resolve to
  // `undefined`, the same conservative "hide" default as before this slice —
  // mirrors the try/catch-to-null pattern in `usePgServerMajorVersion`.
  const versionQuery = useQuery<string | undefined>({
    queryKey: ['dashboard-template-server-version', connectionId, engine, database],
    queryFn: async () => {
      if (!connectionId || !engine) return undefined;
      try {
        const info = await fetchServerVersionInfo(connectionId, engine, database);
        return info.version;
      } catch {
        return undefined;
      }
    },
    enabled: Boolean(connectionId && engine && engine !== 'kusto'),
    staleTime: FIFTEEN_MINUTES,
  });

  const capabilities = useMemo(
    () => templateCapabilities(connectionId, engine, versionQuery.data),
    [connectionId, engine, versionQuery.data],
  );

  const templates = useMemo(
    () => (engine ? availableTemplates(engine, versionQuery.data, capabilities, query) : []),
    [engine, versionQuery.data, capabilities, query],
  );

  if (!engine) {
    return (
      <div
        className="flex min-h-0 flex-1 items-center justify-center rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground"
        data-testid="add-tile-templates-empty"
      >
        Pick a connection to see its template catalog — each engine (SQL Server, PostgreSQL, MySQL,
        Oracle, SQLite, Kusto) has its own.
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex items-center gap-2">
        <Input
          className="h-7 flex-1 text-xs"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search templates…  (waits, blocking, tempdb, backups…)"
          data-testid="add-tile-templates-search"
        />
        <span
          className="shrink-0 text-[11px] text-muted-foreground"
          data-testid="add-tile-templates-count"
        >
          {templates.length} {templates.length === 1 ? 'template' : 'templates'}
        </span>
      </div>
      <div className="grid min-h-0 flex-1 auto-rows-min grid-cols-1 gap-1.5 overflow-y-auto pr-1 sm:grid-cols-2">
        {templates.map((t) => (
          <TemplateCard
            key={t.id}
            template={t}
            selected={t.id === selectedTemplateId}
            onSelect={onSelectTemplate}
          />
        ))}
        {templates.length === 0 && (
          <p className="col-span-full py-4 text-center text-xs text-muted-foreground">
            No templates match "{query}".
          </p>
        )}
      </div>
      <span className="text-[11px] text-muted-foreground">
        Pick one to read its SQL and permissions — it inserts as plain, editable SQL, so nothing
        about how the tile runs is hidden.
      </span>
    </div>
  );
}
